import React from 'react';
import { Modal, Loading } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import useDeleteSingleListEntry from 'Hooks/query-hooks/listEntry/useDeleteSingleListEntry';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import styles from './DeleteListEntryModal.css';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    listEntryId: number;
    listId: number;
    listName: string;
    rowData: RangeList;
};

const DeleteListEntryModal = ({ isOpen, onClose, listEntryId, listId, listName, rowData }: Props) => {
    const { t } = useTranslation();
    const { isLoading, mutate } = useDeleteSingleListEntry({
        rowData,
        listEntryId,
        listId,
        listName,
        mutationOptions: {
            onSuccess: () => {
                onClose();
            },
            onError: () => {
                onClose();
            }
        }
    });

    const handleOnAction = () => {
        mutate();
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Lists - Delete List Entry',
                text: 'Delete List Entry'
            }
        });
    };

    return (
        <Modal
            dataTestId='delete-entry-modal'
            isOpen={isOpen}
            closeModal={onClose}
            status='warning'
            containerClass={styles.modalContainer}
        >
            <Modal.Title id='delete-entry-title' title={t('Delete Entry')} statusIcon='warning' onClose={onClose} />
            <div className={styles.deleteWarningContent}>
                <div data-testid='delete-entry-with-id' className={styles.entryInfo}>
                    {t('delete entry warning')}
                </div>
                {t('This cannot be undone You will have to add this entry again')}.
            </div>
            <Modal.Footer
                id='delete-entry-footer'
                onAction={handleOnAction}
                disableOnCancel={isLoading}
                disableOnAction={isLoading}
                onActionText={
                    isLoading ? (
                        <div data-testid='delete-loader'>
                            <Loading size='sm' fillName='white' />
                        </div>
                    ) : (
                        t('Remove Entry')
                    )
                }
                onClose={onClose}
                onCloseText={t('Cancel')}
            />
        </Modal>
    );
};

export default DeleteListEntryModal;
